import { useSignals } from "@preact/signals-react/runtime";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetDrivers } from "../api/driversApi";
import { useDayForecast, useWeekForecast, useYearForecast, } from "../api/forecastApi";
import { DEFAULT_COUNTRY_CODE } from "../forecastConstants";
import { selectedForecastDate$ } from "../state/forecastDate";
import { buildForecastTableRows, isAggregateSubType, transformDayForecast, transformWeekForecast, transformYearForecast, } from "../utils/forecastDataTransform";
import { getFiscalYearFromDate } from "../utils/forecastDateUtils";
const extractUnitRawId = (orgNode) => {
    if (!orgNode.startsWith("unit:"))
        return undefined;
    return orgNode.slice("unit:".length);
};
export const useForecastDataHandler = (params) => {
    const { forecastApi, orgNode, countryCode, unitId: unitIdParam, divisionId, currentView, selectedDriver, setSelectedDriver, } = params;
    useSignals();
    const queryClient = useQueryClient();
    const unitId = useMemo(() => {
        if (unitIdParam != null)
            return unitIdParam;
        if (orgNode === undefined || orgNode === "") {
            return undefined;
        }
        const rawId = extractUnitRawId(orgNode);
        if (rawId === undefined) {
            return undefined;
        }
        const parsed = parseInt(rawId, 10);
        if (Number.isNaN(parsed)) {
            return undefined;
        }
        return parsed;
    }, [orgNode, unitIdParam]);
    const selectedDate = selectedForecastDate$.value;
    const effectiveCountryCode = countryCode ?? DEFAULT_COUNTRY_CODE;
    const { data: countryDrivers, loading: driversLoading, error: driverError, refetch: refetchDrivers, } = useGetDrivers({
        fetchApi: forecastApi,
        countryCode: effectiveCountryCode ?? "",
        unitId: unitId?.toString() ?? "",
        hasForecast: true,
        divisionId: divisionId?.toString() ?? "",
        enabled: (unitId != null || divisionId != null) && Boolean(effectiveCountryCode),
    });
    const driverList = useMemo(() => {
        if (!countryDrivers)
            return [];
        return countryDrivers.map((driver) => ({
            id: driver.driverId,
            displayName: driver.displayName,
            label: driver.label,
            name: driver.name,
            subType: driver.subType,
            displaySubType: driver.displaySubType,
            unitOfMeasurement: driver.unitOfMeasurement ?? "",
        }));
    }, [countryDrivers]);
    const hasDrivers = driverList.length > 0;
    const [internalSelectedDriver, internalSetSelectedDriver] = useState(undefined);
    const effectiveSelectedDriver = selectedDriver ?? internalSelectedDriver ?? driverList[0];
    const setEffectiveSelectedDriver = setSelectedDriver ?? internalSetSelectedDriver;
    const firstDriver = driverList[0];
    useEffect(() => {
        if (!effectiveSelectedDriver && hasDrivers) {
            const first = driverList[0];
            if (first) {
                const sameNameDrivers = driverList.filter((d) => d.name === first.name);
                const allDriver = sameNameDrivers.find((d) => isAggregateSubType(d.subType));
                setEffectiveSelectedDriver(allDriver ?? first);
            }
        }
    }, [
        effectiveSelectedDriver,
        hasDrivers,
        driverList,
        firstDriver,
        setEffectiveSelectedDriver,
    ]);
    const selectedDriverName = effectiveSelectedDriver?.name ?? "";
    const driverSubTypesForName = useMemo(() => {
        if (!selectedDriverName)
            return [];
        return Array.from(new Set(driverList
            .filter((d) => d.name === selectedDriverName)
            .map((d) => d.subType)
            .filter((st) => st != null)));
    }, [driverList, selectedDriverName]);
    const selectedDriverSubType = useMemo(() => {
        const driverSubType = effectiveSelectedDriver?.subType;
        if (driverSubTypesForName.length === 1) {
            return driverSubTypesForName[0];
        }
        if (isAggregateSubType(driverSubType) === true)
            return undefined;
        return driverSubType;
    }, [effectiveSelectedDriver?.subType, driverSubTypesForName]);
    const hasValidDriverName = selectedDriverName.trim() !== "";
    const numericUnitId = unitId;
    const currentDate = selectedDate;
    const weekStartDate = useMemo(() => {
        if (!currentDate)
            return "";
        return currentDate.slice(0, 10);
    }, [currentDate]);
    const dayForecast = useDayForecast(forecastApi, {
        unitId: numericUnitId,
        date: currentDate,
        driverName: selectedDriverName,
        driverSubType: selectedDriverSubType,
    }, undefined, currentView === "day" && hasValidDriverName && numericUnitId != null);
    const weekForecast = useWeekForecast(forecastApi, {
        unitId: numericUnitId,
        weekStartDate,
        driverName: selectedDriverName,
        driverSubType: selectedDriverSubType,
    }, undefined, currentView === "week" && hasValidDriverName && numericUnitId != null);
    const fiscalYear = getFiscalYearFromDate(currentDate);
    const onForecastPatched = useCallback(() => {
        if (currentView === undefined || numericUnitId == null)
            return;
        if (selectedDriverName.trim() === "")
            return;
        const siblingViews = ["day", "week", "year"].filter((v) => v !== currentView);
        for (const siblingView of siblingViews) {
            const queryKey = siblingView === "day"
                ? [
                    "day-forecast",
                    numericUnitId,
                    currentDate ?? "",
                    selectedDriverName,
                    selectedDriverSubType,
                ]
                : siblingView === "week"
                    ? [
                        "week-forecast",
                        numericUnitId,
                        weekStartDate,
                        selectedDriverName,
                        selectedDriverSubType,
                    ]
                    : [
                        "year-forecast",
                        numericUnitId,
                        fiscalYear,
                        selectedDriverName,
                        selectedDriverSubType,
                    ];
            void queryClient.invalidateQueries({ queryKey });
        }
    }, [
        queryClient,
        currentView,
        numericUnitId,
        selectedDriverName,
        selectedDriverSubType,
        currentDate,
        weekStartDate,
        fiscalYear,
    ]);
    const yearForecast = useYearForecast(forecastApi, {
        unitId: numericUnitId,
        fiscalYear,
        driverName: selectedDriverName,
        driverSubType: selectedDriverSubType,
    }, undefined, currentView === "year" && hasValidDriverName && numericUnitId != null);
    const { data: rawForecastData, isLoading: forecastLoading, refetch: refetchForecastFn, } = (() => {
        if (currentView === undefined || !hasValidDriverName) {
            return {
                data: undefined,
                isLoading: false,
                refetch: () => { },
            };
        }
        if (currentView === "day")
            return dayForecast;
        if (currentView === "week")
            return weekForecast;
        if (currentView === "year")
            return yearForecast;
        return { data: undefined, isLoading: false, refetch: () => { } };
    })();
    const { forecastData, timeSlots, chartTimeSlots } = useMemo(() => {
        if (!rawForecastData) {
            return {
                forecastData: [],
                timeSlots: [],
                chartTimeSlots: undefined,
            };
        }
        switch (currentView) {
            case "day": {
                const { tableGraphForecastData, timeSlots: slots } = transformDayForecast(rawForecastData);
                return {
                    forecastData: tableGraphForecastData,
                    timeSlots: slots,
                    chartTimeSlots: undefined,
                };
            }
            case "week": {
                const { tableGraphForecastData, timeSlots: slots } = transformWeekForecast(rawForecastData);
                return {
                    forecastData: tableGraphForecastData,
                    timeSlots: slots,
                    chartTimeSlots: undefined,
                };
            }
            case "year": {
                const { tableGraphForecastData, timeSlots: slots } = transformYearForecast(rawForecastData);
                return {
                    forecastData: tableGraphForecastData,
                    timeSlots: slots,
                    chartTimeSlots: undefined,
                };
            }
            default:
                return {
                    forecastData: [],
                    timeSlots: [],
                    chartTimeSlots: undefined,
                };
        }
    }, [rawForecastData, currentView]);
    const isLoading = driversLoading || (!hasValidDriverName && hasDrivers) || forecastLoading;
    const filteredData = useMemo(() => {
        if (!effectiveSelectedDriver)
            return [];
        const hasMultipleSubTypes = driverSubTypesForName.length > 1;
        const hasSelectedSubType = effectiveSelectedDriver?.subType !== undefined;
        const isAllSubType = isAggregateSubType(effectiveSelectedDriver?.subType) === true;
        if (hasMultipleSubTypes && (!hasSelectedSubType || isAllSubType)) {
            const driverLabels = driverList
                .filter((d) => d.name === effectiveSelectedDriver.name)
                .map((d) => d.label);
            return forecastData.filter((row) => driverLabels.includes(row.driverLabel));
        }
        return forecastData.filter((row) => row.driverLabel === effectiveSelectedDriver.label);
    }, [
        forecastData,
        effectiveSelectedDriver,
        driverSubTypesForName,
        driverList,
    ]);
    const selectedDateRef = selectedForecastDate$.value;
    const rawTableRows = useMemo(() => {
        if (!rawForecastData || currentView === undefined)
            return [];
        const refDate = selectedDateRef ?? "";
        const { tableRows: rows } = buildForecastTableRows(rawForecastData, currentView, refDate);
        return rows;
    }, [rawForecastData, currentView, selectedDateRef]);
    const tableRows = useMemo(() => {
        if (rawTableRows == null ||
            rawTableRows.length === 0 ||
            effectiveSelectedDriver == null)
            return undefined;
        const hasMultipleSubTypes = driverSubTypesForName.length > 1;
        const hasSelectedSubType = effectiveSelectedDriver.subType !== undefined;
        const isAllSubType = isAggregateSubType(effectiveSelectedDriver.subType) === true;
        if (hasMultipleSubTypes && (!hasSelectedSubType || isAllSubType)) {
            const driverLabels = driverList
                .filter((d) => d.name === effectiveSelectedDriver.name)
                .map((d) => d.label);
            return rawTableRows.filter((row) => driverLabels.includes(row.driver));
        }
        return rawTableRows.filter((row) => row.driver === effectiveSelectedDriver.label);
    }, [
        rawTableRows,
        effectiveSelectedDriver,
        driverSubTypesForName,
        driverList,
    ]);
    const tableSelectedDate = selectedForecastDate$.value ??
        (currentView === "day" && rawForecastData && "date" in rawForecastData
            ? rawForecastData.date
            : currentView === "week" &&
                rawForecastData &&
                "weekStartDate" in rawForecastData
                ? rawForecastData.weekStartDate
                : format(new Date(), "yyyy-MM-dd"));
    const generatedAt = rawForecastData?.generatedAt ?? null;
    return {
        forecastData,
        timeSlots,
        chartTimeSlots,
        isLoading,
        driverError,
        refetchDrivers,
        driverList,
        selectedDriver: effectiveSelectedDriver,
        selectedDriverName: effectiveSelectedDriver?.name,
        selectedDriverSubType: effectiveSelectedDriver?.subType,
        setSelectedDriver: setEffectiveSelectedDriver,
        filteredData,
        unitId: numericUnitId,
        hasValidDriverName,
        rawForecastData,
        weekStartDate: currentView === "week" ? weekStartDate : currentDate,
        tableRows: tableRows != null && tableRows.length > 0 ? tableRows : undefined,
        forecastApi,
        currentView,
        selectedDate: tableSelectedDate,
        refetchForecast: refetchForecastFn,
        onForecastPatched,
        generatedAt,
    };
};
//# sourceMappingURL=useForecastDataHandler.js.map